/* Copyright (c) 2020-present CloudIO Inc */

import { makeStyles } from '@mui/styles';
import cx from 'clsx';
import React, { memo, useEffect, useRef, useState } from 'react';
import { ItemID, AnyColumn } from 'componentregistry';
import { Theme, Direction } from '@mui/material';
import Cells from './Cells';
import { Cell, CellComponentProps, DataContext } from './types';
import { useScrollValue } from './GridScroll';
import { isNull } from '../../../utils/isEmpty';
import { useGridBounds } from '../Context';

const useStyles = makeStyles((theme: Theme) => ({
  gridHeader: {
    position: 'absolute',
    overflow: 'hidden',
    top: 0,
  },
  gridHeaderCenter: {
    zIndex: 3,
  },
  gridHeaderLeft: {
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
  },
  gridHeaderRight: {
    zIndex: 4,
    backgroundColor: theme.palette.background.paper,
  },
}));

interface Props {
  CellComponent: React.FunctionComponent<CellComponentProps>;
  appUid: string;
  roleUid: string;
  columnLefts: number[];
  columnWidths: number[];
  columns: AnyColumn[];
  custom: boolean;
  dataContext: DataContext;
  direction?: Direction;
  fixedLeftColumns: number;
  fixedRightColumns: number;
  h1FixedLeftColumns: number;
  h1FixedRightColumns: number;
  h2FixedLeftColumns: number;
  h2FixedRightColumns: number;
  h3FixedLeftColumns: number;
  h3FixedRightColumns: number;
  headerRows: number;
  innerWidth: number;
  onClick?: (e: React.MouseEvent<HTMLElement>, cell: Cell<AnyColumn>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  pageId: ItemID;
  resetScroll: () => void;
  rowHeight: number;
  showColumnLines: boolean;
  showRowLines: boolean;
  totalColumns: number;
  vertialScrollbarSize: number;
  totalRows: number;
  h1GroupColumnLefts: number[];
  h1GroupColumnWidths: number[];
  h2GroupColumnLefts: number[];
  h2GroupColumnWidths: number[];
  h3GroupColumnLefts: number[];
  h3GroupColumnWidths: number[];
  h1GroupColumns: AnyColumn[];
  h2GroupColumns: AnyColumn[];
  h3GroupColumns: AnyColumn[];
  bodyHeight?: number;
  bodyWidth?: number;
}

function Header(props: Props): React.ReactElement {
  const {
    CellComponent,
    appUid,
    roleUid,
    columnLefts,
    columnWidths,
    columns,
    custom,
    dataContext,
    direction,
    fixedLeftColumns,
    fixedRightColumns,
    h1FixedLeftColumns = 0,
    h1FixedRightColumns = 0,
    h2FixedLeftColumns = 0,
    h2FixedRightColumns = 0,
    h3FixedLeftColumns = 0,
    h3FixedRightColumns = 0,
    headerRows,
    innerWidth,
    onClick,
    onKeyDown,
    pageId,
    resetScroll,
    rowHeight,
    showColumnLines,
    showRowLines,
    totalColumns,
    vertialScrollbarSize,
    totalRows,
    h1GroupColumns,
    h2GroupColumns,
    h3GroupColumns,
    h1GroupColumnLefts,
    h1GroupColumnWidths,
    h2GroupColumnLefts,
    h2GroupColumnWidths,
    h3GroupColumnLefts,
    h3GroupColumnWidths,
    bodyHeight = 0,
    bodyWidth = 0,
  } = props;
  const classes = useStyles();
  const headerHeight = headerRows
    ? (headerRows - (dataContext.showFilters ? 1 : 0)) * rowHeight
    : 0;

  const leftFixedWidth = fixedLeftColumns
    ? columnLefts[fixedLeftColumns - 1] + columnWidths[fixedLeftColumns - 1]
    : 0;
  const rightFixedWidth = fixedRightColumns
    ? innerWidth - columnLefts[totalColumns - fixedRightColumns]
    : 0;
  const endRow = headerRows - 1 - (dataContext.showFilters ? 1 : 0);

  const mv = useScrollValue();
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    return mv.pubsub.sub('OnChange', async (_event, { x }) => {
      const el = rootRef.current;
      if (el) {
        if (!isNull(x)) {
          // console.log('scrollTo', { x, y, left: el.scrollLeft });

          el.style[direction === 'rtl' ? 'right' : 'left'] = `${
            leftFixedWidth + -1 * -x
          }px`;
        }
      }
    });
  }, [direction, leftFixedWidth, mv.pubsub]);

  const gridBoundsSignal = useGridBounds();
  const [{ left, startColumn, endColumn, showShadow }, setState] = useState<{
    left: number;
    startColumn: number;
    endColumn: number;
    showShadow: boolean;
  }>(() => {
    const bound = gridBoundsSignal.peek();
    return {
      left: bound.pos.left,
      startColumn: bound.bound.column.start,
      endColumn: bound.bound.column.end,
      endRow: bound.bound.row.end,
      showShadow: bound.pos.top > 5,
    };
  });

  useEffect(() => {
    return gridBoundsSignal.pos.onChange(({ left: _left, top }) => {
      setState((s) =>
        s.left === _left ? s : { ...s, left: _left, showShadow: top > 5 },
      );
    });
  }, [gridBoundsSignal]);
  useEffect(() => {
    return gridBoundsSignal.bound.column.onChange((column) => {
      setState((s) => ({
        ...s,
        startColumn: column.start,
        endColumn: column.end,
      }));
    });
  }, [gridBoundsSignal]);

  return (
    <>
      <div
        ref={rootRef}
        className={cx(
          'io-grid-header-main',
          classes.gridHeader,
          classes.gridHeaderCenter,
          showShadow ? 'io-grid-header-shadow' : null,
        )}
        style={{
          height: headerHeight,
          width: innerWidth,
          [direction === 'rtl' ? 'right' : 'left']: leftFixedWidth + -1 * left,
        }}
      >
        <Cells
          CellComponent={CellComponent}
          appUid={appUid}
          roleUid={roleUid}
          columnLefts={columnLefts}
          columnWidths={columnWidths}
          columns={columns}
          custom={custom}
          dataContext={dataContext}
          direction={direction}
          endColumn={endColumn}
          endRow={endRow}
          fixedLeftColumns={fixedLeftColumns}
          headerRows={headerRows}
          onClick={onClick}
          onKeyDown={onKeyDown}
          pageId={pageId}
          region="header-center"
          resetScroll={resetScroll}
          rowHeight={rowHeight}
          showColumnLines={showColumnLines}
          showRowLines={showRowLines}
          startColumn={startColumn}
          startRow={0}
          actualEndRow={endRow}
          actualStartRow={0}
          totalRows={totalRows}
          h1GroupColumnLefts={h1GroupColumnLefts}
          h1GroupColumnWidths={h1GroupColumnWidths}
          h1GroupColumns={h1GroupColumns}
          h2GroupColumnLefts={h2GroupColumnLefts}
          h2GroupColumnWidths={h2GroupColumnWidths}
          h2GroupColumns={h2GroupColumns}
          h3GroupColumnLefts={h3GroupColumnLefts}
          h3GroupColumnWidths={h3GroupColumnWidths}
          h3GroupColumns={h3GroupColumns}
          h1FixedLeftColumns={h1FixedLeftColumns}
          h2FixedLeftColumns={h2FixedLeftColumns}
          h3FixedLeftColumns={h3FixedLeftColumns}
          h1FixedRightColumns={h1FixedRightColumns}
          h2FixedRightColumns={h2FixedRightColumns}
          h3FixedRightColumns={h3FixedRightColumns}
          fixedRightColumns={fixedRightColumns}
          bodyWidth={bodyWidth}
          bodyHeight={bodyHeight}
        />
      </div>
      {fixedLeftColumns > 0 ? (
        <div
          className={cx(
            'io-grid-header-left',
            classes.gridHeader,
            classes.gridHeaderLeft,
            showShadow ? 'io-grid-header-left-fixed-shadow' : null,
          )}
          style={{
            height: headerHeight,
            width: leftFixedWidth,
            [direction === 'rtl' ? 'right' : 'left']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnLefts={columnLefts}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={fixedLeftColumns - 1}
            endRow={endRow}
            fixedLeftColumns={fixedLeftColumns}
            headerRows={headerRows}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="header-left"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={0}
            startRow={0}
            actualEndRow={endRow}
            actualStartRow={0}
            totalRows={totalRows}
            h1GroupColumnLefts={h1GroupColumnLefts}
            h1GroupColumnWidths={h1GroupColumnWidths}
            h1GroupColumns={h1GroupColumns}
            h2GroupColumnLefts={h2GroupColumnLefts}
            h2GroupColumnWidths={h2GroupColumnWidths}
            h2GroupColumns={h2GroupColumns}
            h3GroupColumnLefts={h3GroupColumnLefts}
            h3GroupColumnWidths={h3GroupColumnWidths}
            h3GroupColumns={h3GroupColumns}
            h1FixedLeftColumns={h1FixedLeftColumns}
            h2FixedLeftColumns={h2FixedLeftColumns}
            h3FixedLeftColumns={h3FixedLeftColumns}
            h1FixedRightColumns={h1FixedRightColumns}
            h2FixedRightColumns={h2FixedRightColumns}
            h3FixedRightColumns={h3FixedRightColumns}
            fixedRightColumns={fixedRightColumns}
            bodyWidth={bodyWidth}
            bodyHeight={bodyHeight}
          />
        </div>
      ) : null}
      {fixedRightColumns > 0 ? (
        <div
          className={cx(
            'io-grid-header-right',
            classes.gridHeader,
            classes.gridHeaderRight,
            showShadow ? 'io-grid-header-right-fixed-shadow' : null,
          )}
          style={{
            height: headerHeight,
            width: rightFixedWidth + vertialScrollbarSize,
            [direction === 'rtl' ? 'left' : 'right']: 0,
          }}
        >
          <Cells
            CellComponent={CellComponent}
            appUid={appUid}
            roleUid={roleUid}
            columnWidths={columnWidths}
            columns={columns}
            custom={custom}
            dataContext={dataContext}
            direction={direction}
            endColumn={totalColumns - 1}
            endRow={endRow}
            fixedLeftColumns={fixedLeftColumns}
            h1FixedLeftColumns={h1FixedLeftColumns}
            h2FixedLeftColumns={h2FixedLeftColumns}
            h3FixedLeftColumns={h3FixedLeftColumns}
            headerRows={headerRows}
            onClick={onClick}
            onKeyDown={onKeyDown}
            pageId={pageId}
            region="header-right"
            resetScroll={resetScroll}
            rowHeight={rowHeight}
            showColumnLines={showColumnLines}
            showRowLines={showRowLines}
            startColumn={totalColumns - fixedRightColumns}
            startRow={0}
            actualEndRow={endRow}
            actualStartRow={0}
            totalRows={totalRows}
            h1GroupColumnLefts={h1GroupColumnLefts}
            h1GroupColumnWidths={h1GroupColumnWidths}
            h1GroupColumns={h1GroupColumns}
            h2GroupColumnLefts={h2GroupColumnLefts}
            h2GroupColumnWidths={h2GroupColumnWidths}
            h2GroupColumns={h2GroupColumns}
            h3GroupColumnLefts={h3GroupColumnLefts}
            h3GroupColumnWidths={h3GroupColumnWidths}
            h3GroupColumns={h3GroupColumns}
            h1FixedRightColumns={h1FixedRightColumns}
            h2FixedRightColumns={h2FixedRightColumns}
            h3FixedRightColumns={h3FixedRightColumns}
            fixedRightColumns={fixedRightColumns}
            bodyWidth={bodyWidth}
            bodyHeight={bodyHeight}
          />
        </div>
      ) : null}
    </>
  );
}

export default memo<Props>(Header);
